import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ECharts, EChartOption } from 'echarts/lib/echarts';
import { legendFormatter } from '../../helpers/legend-formatter';
import { ChartValueFormatterService } from '../../services/chart-value-formatter.service';
import yAxisMaxValue from '../../helpers/y-axis-max-value';
import { ChartData, DownloadTypes } from '../../models/chart.model';
import { legendIcon, symbolIcon } from '../../icon-paths';
import saveChartWithTitle from '../../helpers/save-chart-with-title';
import { colorsScss } from '../../helpers/colors-scss';

@Component({
  selector: 'studio-line-chart',
  templateUrl: '../../templates/chart.component.html',
  styleUrls: ['../../styles/chart.scss', '../../styles/tooltip.scss'],
})
export class LineChartComponent implements OnChanges {
  @Input() chartTitle: string;
  @Input() chartData: ChartData[] = [];
  @Input() xAxisLabels: string[] = [];
  @Output() chartDownloaded = new EventEmitter<DownloadTypes>();
  public options: EChartOption = {};
  public chartInstance: ECharts;

  constructor(protected chartValueFormatterService: ChartValueFormatterService) {}

  ngOnChanges() {
    if (this.chartData && this.xAxisLabels) {
      this.createChart();
    }
  }

  onChartInit(instance: ECharts) {
    this.chartInstance = instance;
  }

  createChart() {
    this.options = this.chartConfig();
  }

  chartConfig(): EChartOption {
    // Create a new array, don't mutate the array order
    const chartData = [...this.chartData].reverse();

    return {
      backgroundColor: 'white',
      animationDurationUpdate: 0,
      textStyle: {
        color: colorsScss.primary,
        fontFamily: 'Rubik, "Helvetica Neue", sans-serif',
        fontWeight: 300,
      },
      title: {
        left: 0,
        textStyle: {
          color: colorsScss.primary,
          fontSize: 32,
          fontWeight: 500,
        },
      },
      legend: {
        data: chartData.map((data) => data.name),
        selectedMode: false,
        icon: legendIcon,
        left: 0,
        top: 0,
        itemGap: 30,
        formatter: (name) => {
          const { unit } = chartData.find((data) => data.name === name);
          return legendFormatter(name, unit);
        },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderRadius: 10,
        padding: 0,
        axisPointer: {
          type: 'line',
          lineStyle: {
            type: 'dashed',
            color: colorsScss.primary,
            width: 2,
          },
        },
        textStyle: {
          color: colorsScss.primary,
        },
        formatter: (params) => this.chartValueFormatterService.formatTooltip(params, chartData),
      },
      grid: {
        left: 35,
        top: 60,
        right: 20,
        bottom: 20,
        containLabel: true,
      },
      color: [colorsScss.secondaryLight, colorsScss.primary],
      xAxis: {
        type: 'category',
        offset: 20,
        data: this.xAxisLabels,
        boundaryGap: false,
        splitLine: {
          show: true,
          lineStyle: {
            color: colorsScss.grey,
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          rotate: 45,
          interval: 0,
        },
      },
      yAxis: chartData.map((data) => ({
        type: 'value',
        offset: 20,
        position: 'left',
        splitNumber: 5,
        minInterval: 1,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: colorsScss.grey,
          },
        },
        max: (value) => yAxisMaxValue(value.max, data.type),
        axisLabel: {
          formatter: (value) => this.chartValueFormatterService.formatValue(value, data.type),
        },
      })),
      // Drawn in order, the primary line needs to be last to appear at the highest level
      series: this.chartData.map((data) => ({
        data: data.seriesData,
        name: data.name,
        type: 'line',
        symbolSize: 10,
        symbol: symbolIcon,
        lineStyle: {
          width: 3,
        },
      })),
    };
  }

  protected downloadAsImageClicked() {
    saveChartWithTitle(this.chartInstance, this.chartTitle);
    this.chartDownloaded.emit('image');
  }

  protected downloadAsCsvClicked() {
    this.chartDownloaded.emit('csv');
  }
}
